// Chat pane — useChat-backed interactive transcript + tool-call rendering.
//
// `ChatPaneShell` defers mounting `useChat` until history has been fetched.
// `useChat`'s `messages` prop is initial-state-only — it's not reactive — so
// we have to wait for history to be present before mounting.
//
// Tool-call rendering: AI SDK v6 emits tool parts with shape `tool-<toolName>`.
// Each part carries a `state` field (input-streaming / input-available /
// output-available / output-error) plus the input/output payloads. We have
// three chat tools: save / edit / delete. Each gets a short card with:
//   - icon + verb + the salient field (date/summary for save, eventId for
//     edit/delete) so the user sees at-a-glance what the agent did
//   - a clear failure path when our server-side guard returned {ok: false}
//   - a pending state while the call is streaming
//
// The server's `execute` returns `{ok: false, error}` for the meta-question
// rejection — that lands as `state='output-available'` (the call succeeded,
// the tool just reported a logical failure), so we have to look at
// `output.ok` to render the right colour.

import { useChat } from '@ai-sdk/react';
import { DefaultChatTransport, type UIMessage } from 'ai';
import { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useChatHistory, useChatKickoff } from '../chat-history';
import { useLocale } from '../locale';
import { m } from '../paraglide/messages';
import { useInvalidateProgress } from '../progress';
import { useInvalidateTimeline, useTimeline } from '../timeline';

/**
 * Assistant text rendered as Markdown. We deliberately keep the surface
 * narrow:
 *   - GFM (GitHub-flavored Markdown) for tables, strike, autolinks.
 *   - No raw HTML (default behaviour) — the assistant has no business
 *     emitting raw HTML and rendering it could open an XSS vector if the
 *     content ever made it into a screenshot/share view.
 *   - Links open in a new tab and carry rel="noopener noreferrer". The
 *     model rarely emits links, but better safe.
 *
 * The chat bubble already constrains font-size; the markdown renderer
 * just emits semantic elements that pick up the bubble's typography.
 */
function AssistantMarkdown({ text }: { text: string }) {
  return (
    <div className="md">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ href, children, ...rest }) => (
            <a href={href} target="_blank" rel="noreferrer noopener" {...rest}>
              {children}
            </a>
          ),
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
}

type AnyToolPart = {
  type: string;
  toolCallId?: string;
  state?: string;
  input?: unknown;
  output?: unknown;
  errorText?: string;
};

function isOkOutput(out: unknown): boolean {
  return !!out && typeof out === 'object' && (out as { ok?: unknown }).ok === true;
}

function asRecord(v: unknown): Record<string, unknown> {
  return v && typeof v === 'object' ? (v as Record<string, unknown>) : {};
}

function ToolCallCard({ part }: { part: AnyToolPart }) {
  const input = asRecord(part.input);
  const output = asRecord(part.output);

  // Streaming / pre-execute state.
  if (part.state === 'input-streaming' || part.state === 'input-available') {
    const verb =
      part.type === 'tool-saveTimelineEvent'
        ? m.toolCardRecording()
        : part.type === 'tool-editTimelineEvent'
          ? m.toolCardEditing()
          : part.type === 'tool-saveCaseFact'
            ? 'Saving fact…'
            : part.type === 'tool-resolveInconsistency'
              ? 'Resolving conflict…'
              : m.toolCardRemoving();
    return (
      <div className="tool-card">
        <span className="tool-card-pending">{verb}</span>
      </div>
    );
  }

  // Tool-level transport error (network, schema validation failed, etc.).
  if (part.state === 'output-error') {
    return (
      <div className="tool-card">
        <span className="tool-card-err">
          {m.toolCardFailed({ error: String(part.errorText ?? m.toolCardUnknownError()) })}
        </span>
      </div>
    );
  }

  // Logical failure returned by our `execute` — most often the meta-question
  // guard on saveTimelineEvent.
  if (part.state === 'output-available' && !isOkOutput(part.output)) {
    return (
      <div className="tool-card">
        <span className="tool-card-err">
          {m.toolCardRejected({ error: String(output.error ?? m.toolCardRejectedFallback()) })}
        </span>
      </div>
    );
  }

  // Success path — branch by tool type.
  if (part.type === 'tool-saveTimelineEvent') {
    // The server's response is `{ok, id, stored: TimelineEventInput}`; show
    // the date + summary so the user can spot a wrong save immediately.
    const stored = asRecord(output.stored);
    const date = typeof stored.date === 'string' ? stored.date : null;
    const summary = typeof stored.summary === 'string' ? stored.summary : null;
    return (
      <div className="tool-card">
        <span className="tool-card-ok">
          {m.toolCardRecorded()}
          {date ? ` · ${date}` : ''}
          {summary ? ` · ${summary}` : ''}
        </span>
      </div>
    );
  }

  if (part.type === 'tool-editTimelineEvent') {
    // Server returns the post-edit event under `output.updated`; show date +
    // summary so the user sees what the event looks like now.
    const updated = asRecord(output.updated);
    const date = typeof updated.date === 'string' ? updated.date : null;
    const summary = typeof updated.summary === 'string' ? updated.summary : null;
    const changedFields = Object.keys(input).filter(
      (k) => k !== 'eventId' && input[k] !== undefined,
    );
    return (
      <div className="tool-card">
        <span className="tool-card-ok">
          {m.toolCardUpdated()}
          {date ? ` · ${date}` : ''}
          {summary ? ` · ${summary}` : ''}
          {!summary && changedFields.length > 0
            ? ` · ${m.toolCardChanged({ fields: changedFields.join(', ') })}`
            : ''}
        </span>
      </div>
    );
  }

  if (part.type === 'tool-deleteTimelineEvent') {
    // Server captures the pre-delete event under `output.removed`.
    const removed = asRecord(output.removed);
    const date = typeof removed.date === 'string' ? removed.date : null;
    const summary = typeof removed.summary === 'string' ? removed.summary : null;
    return (
      <div className="tool-card">
        <span className="tool-card-ok">
          {m.toolCardRemoved()}
          {date ? ` · ${date}` : ''}
          {summary ? ` · ${summary}` : ''}
        </span>
      </div>
    );
  }

  if (part.type === 'tool-saveCaseFact') {
    const path = typeof input.path === 'string' ? input.path : '';
    const stored = output.stored;
    const value =
      stored == null
        ? '(cleared)'
        : typeof stored === 'object'
          ? JSON.stringify(stored)
          : String(stored);
    return (
      <div className="tool-card">
        <span className="tool-card-ok">
          Saved · {path} = {value}
        </span>
      </div>
    );
  }

  if (part.type === 'tool-resolveInconsistency') {
    const note = typeof input.resolution_note === 'string' ? input.resolution_note : '';
    return (
      <div className="tool-card">
        <span className="tool-card-ok">
          Resolved conflict
          {note ? ` · "${note.length > 80 ? `${note.slice(0, 77)}…` : note}"` : ''}
        </span>
      </div>
    );
  }

  return null;
}

// If history loads empty AND the case has timeline events on file, we ask
// the server to generate the agent's opening message (kickoff) and wait for
// it before mounting useChat. The server is idempotent, so this is safe to
// call on every fresh mount — but we throttle with `hasFiredRef` so we don't
// hammer it on remounts during dev.
export function ChatPaneShell({ caseId }: { caseId: string }) {
  const history = useChatHistory(caseId);
  const timeline = useTimeline(caseId);
  const kickoff = useChatKickoff(caseId);
  const hasFiredRef = useRef(false);

  const messages = history.data ?? [];
  const events = timeline.data ?? [];
  const shouldKickoff =
    history.isSuccess &&
    timeline.isSuccess &&
    messages.length === 0 &&
    events.length > 0 &&
    !hasFiredRef.current &&
    !kickoff.isPending;

  // biome-ignore lint/correctness/useExhaustiveDependencies: only fire on the trigger condition
  useEffect(() => {
    if (shouldKickoff) {
      hasFiredRef.current = true;
      kickoff.mutate();
    }
  }, [shouldKickoff]);

  // While we're either loading history, or waiting for the kickoff to land
  // (which will then refetch history), show a calm waiting state. This is
  // the only UI moment where the user might wait a few seconds; flash-lite
  // typically responds within ~3-5s.
  const waitingForKickoff = kickoff.isPending || (shouldKickoff && messages.length === 0);

  if (history.isLoading || waitingForKickoff) {
    return (
      <div className="chat" aria-busy="true">
        <div className="chat-head">
          <strong>{m.chatHeader()}</strong>
          <span className="small muted">
            {m.chatCaseLabel()} <span className="mono">{caseId.slice(0, 8)}</span>…
          </span>
        </div>
        <div className="chat-empty muted small">
          {waitingForKickoff ? m.chatReviewingCase() : m.chatLoadingHistory()}
        </div>
      </div>
    );
  }

  return <ChatPane caseId={caseId} initialMessages={messages as UIMessage[]} />;
}

function ChatPane({
  caseId,
  initialMessages,
}: {
  caseId: string;
  initialMessages: UIMessage[];
}) {
  const invalidateTimeline = useInvalidateTimeline();
  const invalidateProgress = useInvalidateProgress();
  // Tell the agent which language to reply in. DefaultChatTransport runs its
  // own fetch and bypasses `apiFetch`, so we have to pass headers explicitly
  // — without this, the api falls back to English regardless of the EN/PT
  // toggle (the `X-UI-Locale` header sent by every other request is gone).
  const [locale] = useLocale();
  const { messages, sendMessage, status, error } = useChat({
    messages: initialMessages,
    transport: new DefaultChatTransport({
      api: `/api/cases/${caseId}/chat`,
      headers: { 'X-UI-Locale': locale },
    }),
    // After the assistant finishes (incl. tool calls), refresh both the
    // timeline and the progress card. The consistency check runs server-side
    // fire-and-forget; the progress query polls until it lands.
    onFinish: () => {
      invalidateTimeline(caseId);
      invalidateProgress(caseId);
    },
    onError: (e) => console.error('chat error', e),
  });
  const [input, setInput] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const isBusy = status === 'submitted' || status === 'streaming';

  // Keep the latest message in view as tokens stream in.
  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll on every messages tick
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, status]);

  // Refocus the input when the agent finishes replying so the user can keep typing.
  useEffect(() => {
    if (status === 'ready') {
      inputRef.current?.focus();
    }
  }, [status]);

  // Listen for cross-component "prefill chat" events. The inconsistencies
  // banner emits this when the user clicks "Ask the assistant" — we drop
  // the prefilled text into the input and focus it. The user can edit
  // before sending.
  useEffect(() => {
    function onPrefill(e: Event) {
      const detail = (e as CustomEvent<{ text: string }>).detail;
      if (!detail?.text) return;
      setInput(detail.text);
      inputRef.current?.focus();
      // Move caret to end so typing continues naturally.
      requestAnimationFrame(() => {
        const el = inputRef.current;
        if (el) {
          el.selectionStart = el.value.length;
          el.selectionEnd = el.value.length;
        }
      });
    }
    window.addEventListener('claimpack:chat-prefill', onPrefill);
    return () => window.removeEventListener('claimpack:chat-prefill', onPrefill);
  }, []);

  return (
    <div className="chat">
      <div className="chat-head">
        <strong>{m.chatHeader()}</strong>
        <span className="small muted">
          {m.chatCaseLabel()} <span className="mono">{caseId.slice(0, 8)}</span>…
        </span>
      </div>
      <div className="chat-messages" role="log" aria-live="polite" aria-relevant="additions">
        {messages.length === 0 ? (
          <div className="chat-empty muted small">
            {m.chatEmptyPrompt()}
            <br />
            <em>{m.chatEmptyExample()}</em>
          </div>
        ) : null}
        {messages.map((mm) => (
          <div key={mm.id} className={`bubble ${mm.role}`}>
            {mm.parts.map((p, i) => {
              // Parts are stable per message id; index is the right key.
              if (p.type === 'text') {
                // Assistant replies render as Markdown so the kickoff
                // message and any later structured replies (bullets,
                // headings) render correctly. User bubbles stay plain
                // to avoid surprises if a user pastes Markdown they
                // didn't mean to format.
                if (mm.role === 'assistant') {
                  // biome-ignore lint/suspicious/noArrayIndexKey: stable per-message id
                  return <AssistantMarkdown key={`p-${i}`} text={p.text} />;
                }
                // biome-ignore lint/suspicious/noArrayIndexKey: stable per-message id
                return <span key={`p-${i}`}>{p.text}</span>;
              }
              if (
                p.type === 'tool-saveTimelineEvent' ||
                p.type === 'tool-editTimelineEvent' ||
                p.type === 'tool-deleteTimelineEvent' ||
                p.type === 'tool-saveCaseFact' ||
                p.type === 'tool-resolveInconsistency'
              ) {
                return <ToolCallCard key={p.toolCallId ?? `tc-${i}`} part={p} />;
              }
              return null;
            })}
          </div>
        ))}
        {/* Typing indicator while waiting for the first token of a reply. */}
        {status === 'submitted' ? (
          <div className="bubble assistant" aria-busy="true">
            <span className="typing-dots" aria-label={m.chatThinking()}>
              <i />
              <i />
              <i />
            </span>
          </div>
        ) : null}
        {/* Surface errors (e.g. provider rate-limits) so the user knows what happened. */}
        {error ? (
          <div className="notice error chat-error" role="alert">
            {/Too Many|429|RESOURCE_EXHAUSTED|quota/i.test(error.message)
              ? m.chatRateLimited()
              : m.chatErrorGeneric({ message: error.message })}
          </div>
        ) : null}
        <div ref={messagesEndRef} />
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const t = input.trim();
          if (!t || isBusy) return;
          sendMessage({ text: t });
          setInput('');
          // Refocus immediately so the user can keep typing while the reply streams.
          inputRef.current?.focus();
        }}
        className="chat-form"
      >
        <label htmlFor="chat-input" className="visually-hidden">
          {m.chatPlaceholderType()}
        </label>
        <input
          id="chat-input"
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={isBusy ? m.chatPlaceholderWaiting() : m.chatPlaceholderType()}
          aria-label={m.chatPlaceholderType()}
        />
        <button type="submit" disabled={isBusy || !input.trim()}>
          {m.chatSend()}
        </button>
      </form>
    </div>
  );
}
